import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/Topbar';
import { SkipLink } from '@/components/SkipLink';
import { BuddyMascot } from '@/components/buddies';
import { useProgress } from '@/contexts/ProgressContext';
import { restartOnboarding, skipOnboarding } from '@/onboarding/onboarding-state';

/**
 * Welcome screen · v2 «Знакомство со студией» (slot_t00_ftue_rewrite · 6 JUN 2026).
 *
 * Source design: `04_DESIGN/QA Studio Design System6.7/polished_prototype_riso/`
 * screens-entry.jsx Welcome (L321-366) + screens.css `.welcome-v2` block +
 * styles.css fit-no-scroll rules — Genie's claude-design chat output,
 * external tester feedback round.
 *
 * Replaces the Phase B asym hero+aside layout entirely (welcome-asym /
 * welcome-hero / welcome-buddy / welcome-aside markup retired · их CSS в
 * globals.css оставлен нетронутым). One `.wv-card`: Buddy peek · chip
 * «твой первый тикет · T00» · title · desc · meta dots · foot with CTA.
 *
 * Wiring (App.tsx unchanged): «Открыть тикет» → onStart → goTicket('T00');
 * `.wv-skip` → onSkip → goDashboard. Both call markWelcomeSeen() first.
 * buddyOn gates only the corner peek (as in prototype).
 *
 * i18n keys retained: welcome.eyebrow / ctaStart / ctaSkip.
 * Hidden from NavShell (welcome ∈ HIDDEN_NAV_SCREENS · A1).
 */
interface WelcomeProps {
  onStart: () => void;
  onSkip: () => void;
  buddyOn?: boolean;
  onLogo?: () => void;
}

export function Welcome({ onStart, onSkip, buddyOn = true, onLogo }: WelcomeProps) {
  const { markWelcomeSeen } = useProgress();
  const { t } = useTranslation();

  const handleStart = () => {
    // Re-arm the tutorial state: clears stale dismiss-flags a previous account
    // left in this browser (onboarding-local key is per-device, not per-account
    // — Genie 7 JUN: «туториал перестал показываться вообще»).
    restartOnboarding();
    markWelcomeSeen();
    onStart();
  };
  const handleSkip = () => {
    // know-it-all path: suppress Студия-туториал entirely (Genie 7 JUN).
    skipOnboarding();
    markWelcomeSeen();
    onSkip();
  };

  const noop = () => {};
  const logoHandler = onLogo ?? noop;

  return (
    <div className="screen">
      <SkipLink />
      <Topbar
        crumb={[
          { label: 'Studio', onClick: logoHandler },
          { label: 'День 1 · знакомство', here: true },
        ]}
        onLogo={logoHandler}
        showTimer={false}
      />
      <main id="main-content" className="welcome-main welcome-v2" tabIndex={-1}>
        <div className="wv-wm" aria-hidden="true">
          01
        </div>
        <div className="wv-wrap">
          <span className="wv-eyebrow">
            <span className="dot" aria-hidden="true" />
            {t('welcome.eyebrow', { defaultValue: 'День 1 · твой первый заход' })}
          </span>

          <article className="wv-card">
            {buddyOn && (
              <span className="wv-peek" aria-hidden="true">
                <BuddyMascot size={46} state="happy" />
              </span>
            )}
            {/* Акварельное трио (mascots watercolor line · 7 JUL 2026) — обложка
                карточки; на низких вьюпортах прячется CSS'ом (fit-no-scroll). */}
            <img
              className="wv-cover"
              src="/mascots/trio_wc.jpg"
              alt="Хут, Капи и Ося — напарники курса"
              width={1024}
              height={559}
            />
            <span className="wv-chip">твой первый тикет · T00</span>
            <h1 className="wv-title">Знакомство со&nbsp;студией</h1>
            <p className="wv-desc">
              Разберёмся, что такое тикет и как довести его до «готово». Семь минут — и ты уже
              внутри настоящего процесса.
            </p>
            <div className="wv-meta">
              <span className="s">≈ 7 минут</span>
              <span className="s">урок</span>
              <span className="s">Buddy рядом</span>
            </div>
            <div className="wv-div" />
            <div className="wv-foot">
              <span className="by">
                <span className="by-ava" aria-hidden="true">
                  <BuddyMascot size={28} state="idle" />
                </span>
                Buddy проведёт за&nbsp;руку
              </span>
              <button className="wv-open" type="button" onClick={handleStart}>
                {t('welcome.ctaStart', { defaultValue: 'Открыть тикет' })}{' '}
                <span className="a" aria-hidden="true">
                  →
                </span>
              </button>
            </div>
          </article>

          <button className="wv-skip" type="button" onClick={handleSkip}>
            {t('welcome.ctaSkip', { defaultValue: 'я уже всё это знаю — пропустить' })}
          </button>
        </div>
      </main>
    </div>
  );
}
